import { stopImpersonateUser } from 'admin/services/actions'
import { impersonationSelector } from 'admin/services/selectors'
import { FacebookLoginResponse } from 'interfaces'
import { tap } from 'rxjs/operators'
import { formatProducts, setAddOns, setProducts } from 'services/product'
import { logoutAction, setTrialStatus } from 'services/shared/actions'
import { message } from 'services/snackbar'
import { updateIntercom } from 'services/tracking/login'
import { checkSubscriptionSuccess } from 'services/users/actions'
import { accessTokenSelector } from 'services/users/selectors'
import { PPProduct } from 'shared'
import { HTTP_STATUS_UNAUTHORIZED, LOGOUT } from 'shared/constants'
import { StoreThunkAction } from 'store/state'
import { legacyLogout } from './legacy/logout'
import { V1 } from './net'

const AUTO_LOGOUT_MESSAGE = 'Oops. An error occurred and you\'ve been logged out. Please log back in to continue.'
const AUTO_LOGOUT_MESSAGE_TIMEOUT = 3500
// eslint-disable-next-line no-magic-numbers
export const CHECK_TOKENS_INTERVAL_DEFAULT = 30 * 60 * 1000 // ms (30 minutes)

export * from './login/actions'

export function logout(callback?: (e?: Error) => void): StoreThunkAction<void> {
  console.debug('logging out...')
  return (dispatch, getState) => {
    const token = accessTokenSelector(getState())
    dispatch(stopImpersonateUser())
    dispatch(updateIntercom())
      .finally(() => {
        dispatch(legacyLogout(token || undefined))
          .subscribe(() => {
            if (callback) {
              callback()
            }
          }, (e: Error) => {
            console.log('[logout()] Error: ', e)
            if (callback) {
              callback(e)
            }
          })
      })

    dispatch({ type: LOGOUT })
    dispatch(logoutAction())
  }
}

export const ACTION_CHECK_TOKENS_SUCCESS = 'ACTION_CHECK_TOKENS_SUCCESS'
export const ACTION_CHECK_TOKENS_FAILURE = 'ACTION_CHECK_TOKENS_FAILURE'
export const ACTION_SKIP_TOKENS_REFRESH = 'ACTION_SKIP_TOKENS_REFRESH'

export function skipTokensRefresh(dismissDialog?: boolean) {
  return {
    type: ACTION_SKIP_TOKENS_REFRESH,
    payload: { dismissDialog: Boolean(dismissDialog) }
  }
}

export function checkTokens(): StoreThunkAction<any> {
  return (dispatch, getState) => {
    const state = getState()
    const isImpersonating = impersonationSelector(state) !== undefined

    return dispatch(V1.checkTokens()).pipe(
      tap({
        next: (response: any) => {
          const products = formatProducts(response.subscriptions.products as PPProduct[])
          const user = response.user
          // EXPL: Products and add-ons are for whoever's token made the request, so they refresh while impersonating too.
          // The rest is the admin's own user state, which impersonating must leave alone.
          dispatch(setProducts(products))
          dispatch(setAddOns({ list: response.subscriptions.addons || [], purchased: user.account.addons || {} }))
          if (isImpersonating) {
            return
          }
          const productId = user.account.product
          const productInfo = products.find(p => p.id === productId)
          dispatch(checkSubscriptionSuccess(
            productId,
            productInfo as PPProduct,
            {
              isAdmin: Boolean(user.roles?.isAdmin),
              isBeta: Boolean(user.roles?.isBeta),
              hasThreads: Boolean(user.roles?.hasThreads),
              igDirectEnabled: Boolean(user.account?.config?.igDirect),
              manualInsightsRefresh: Boolean(user.account?.config?.manualInsightsRefresh || user.roles?.manualInsightsRefresh),
              hasMultipleComments: Boolean(user.roles?.hasMultipleComments)
            },
            user
          ))
          dispatch(setTrialStatus({
            trial: user.account.trial,
            status: user.account.status,
            hasPaymentInfo: user.account.hasPaymentInfo,
            chargifySubscriptionId: user.account.chargifySubscriptionId
          }))
          const expiredTokens = Object.keys(user.tokensExpired).reduce((tokensMap: Record<string, any[]>, network: string) => {
            if (Array.isArray(user.tokensExpired[network])) {
              tokensMap[network] = user.tokensExpired[network]
            } else {
              tokensMap[network] = [{ name: network }]
            }

            return tokensMap
          }, {})
          dispatch({ type: ACTION_CHECK_TOKENS_SUCCESS, payload: expiredTokens })
        },
        error: error => {
          dispatch({ type: ACTION_CHECK_TOKENS_FAILURE, payload: error })
          if (error.status === HTTP_STATUS_UNAUTHORIZED) {
            // There's something wrong with user auth, log them out.
            dispatch(message(AUTO_LOGOUT_MESSAGE, 'error', AUTO_LOGOUT_MESSAGE_TIMEOUT))
          } else if (error.response.error.message) {
            dispatch(message(error.response.error.message, 'error', AUTO_LOGOUT_MESSAGE_TIMEOUT))
          }
          dispatch(logout())
        }
      })
    )
  }
}

export const ACTION_UPDATE_FB_LOGIN_STATUS = 'ACTION_UPDATE_FB_LOGIN_STATUS'
export function updateFacebookLoginStatus(status: FacebookLoginResponse) {
  return {
    type: ACTION_UPDATE_FB_LOGIN_STATUS,
    payload: status
  }
}
